import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const DEFAULT_THRESHOLD = 80;

/**
 * scroll container ref, at-bottom tracking, auto-scroll after new content when user was at bottom,
 * and pending "new messages" count when user is scrolled up.
 */
export function useAgentChatScroll(
  messageCount: number,
  layoutBumpKey?: string | number | null,
  options?: { bottomThreshold?: number },
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const threshold = options?.bottomThreshold ?? DEFAULT_THRESHOLD;
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const [pendingNewCount, setPendingNewCount] = useState(0);

  const updateIsAtBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance <= threshold;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) {
      setPendingNewCount(0);
    }
  }, [threshold]);

  const onScroll = useCallback(() => {
    updateIsAtBottom();
  }, [updateIsAtBottom]);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          updateIsAtBottom();
        }
      });
    },
    [updateIsAtBottom],
  );

  // new messages while scrolled up → increment pill count
  useEffect(() => {
    const prev = prevMessageCountRef.current;
    const delta = messageCount - prev;
    prevMessageCountRef.current = messageCount;
    if (delta <= 0) return;
    if (!isAtBottomRef.current) {
      setPendingNewCount((c) => c + delta);
    }
  }, [messageCount]);

  // layout bumps (typing row, etc.) — scroll if still pinned to bottom
  const bumpSerialized =
    layoutBumpKey === undefined || layoutBumpKey === null
      ? ""
      : String(layoutBumpKey);

  useLayoutEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    updateIsAtBottom();
  }, [messageCount, bumpSerialized, updateIsAtBottom]);

  // single-rAF safety net for late layout (e.g. typing row mounting) — the
  // layout effect above already pins synchronously, so one frame is enough.
  // A second nested rAF used to live here and fought the keyboard on mobile.
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el || !isAtBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
      updateIsAtBottom();
    });
    return () => cancelAnimationFrame(id);
  }, [messageCount, bumpSerialized, updateIsAtBottom]);

  return {
    scrollRef,
    isAtBottom,
    onScroll,
    scrollToBottom,
    pendingNewCount,
  };
}
